import {
  Cancel,
  ThreeYearsCancellation,
  TwoYearsCancellation,
} from '@storefront/types/Product';
import { Table } from '@veneer/core';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '@storefront/utils/priceHandler';

import styles from '../faq.module.scss';
import { NamespacesI18n } from '@storefront/i18n';

interface FaqCancellationTableProps {
  cancellationList: Cancel[];
  hasThreeYearsCancellationSku?: boolean;
  unreturnedPrinterFee?: boolean;
}

export const FaqCancellationTable = (props: FaqCancellationTableProps) => {
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const {
    cancellationList,
    hasThreeYearsCancellationSku,
    unreturnedPrinterFee,
  } = props;
  cancellationList.sort(
    (a, b) =>
      Number(a?.cancellation[1]['31D-12M']?.fee?.value) -
      Number(b?.cancellation[1]['31D-12M']?.fee?.value)
  );
  const generalColumns = [
    {
      id: 'printer',
      label: 'Printer',
    },
    {
      id: 'upToOneYear',
      label: t('faq.printer.table.upToOneYear'),
    },
    {
      id: 'upToTwoYears',
      label: t('faq.printer.table.upToTwoYears'),
    },
  ];

  const twoYearsColumn = [
    ...generalColumns,
    {
      id: 'afterTwoYears',
      label: t('faq.printer.table.afterTwoYears'),
    },
  ];

  const threeYearsColumn = [
    ...generalColumns,
    {
      id: 'upToThreeYears',
      label: t('faq.printer.table.upToThreeYears'),
    },
    {
      id: 'afterThreeYears',
      label: t('faq.printer.table.afterThreeYears'),
    },
  ];

  const unreturnedPrinterFeeColumns = [
    {
      id: 'printer',
      label: 'Printer',
    },
    {
      id: 'unreturnedPrinterFee',
      label: t('faq.printer.table.unreturnedPrinterFee'),
    },
  ];

  const twoYearsInfoData = cancellationList.map((cancel) => {
    const printer = cancel.name;

    const upToOneYear = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[1]?.['31D-12M']?.fee?.value
    }`;
    const upToTwoYears = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[2]?.['13M-24M']?.fee?.value
    }`;

    let afterTwoYears = '';
    if ('25M-_' in cancel.cancellation[3]) {
      afterTwoYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as TwoYearsCancellation)?.[3]?.['25M-_']?.fee
          ?.value
      }`;
    } else if ('25M-36M' in cancel.cancellation[3]) {
      afterTwoYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as ThreeYearsCancellation)?.[3]?.['25M-36M']?.fee
          ?.value
      }`;
    }

    return {
      printer,
      upToOneYear,
      upToTwoYears,
      afterTwoYears,
    };
  });

  const threeYearsInfoData = cancellationList.map((cancel) => {
    const printer = cancel.name;

    const upToOneYear = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[1]?.['31D-12M']?.fee?.value
    }`;
    const upToTwoYears = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[2]?.['13M-24M']?.fee?.value
    }`;

    let upToThreeYears = '';
    let afterThreeYears = '';

    if ('25M-36M' in cancel.cancellation[3]) {
      upToThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as ThreeYearsCancellation)?.[3]?.['25M-36M']?.fee
          ?.value
      }`;
      afterThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as ThreeYearsCancellation)?.[4]?.['37M-_']?.fee
          ?.value
      }`;
    } else if ('25M-_' in cancel.cancellation[3]) {
      upToThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as TwoYearsCancellation)?.[3]?.['25M-_']?.fee
          ?.value
      }`;
      afterThreeYears = upToThreeYears;
    }

    return {
      printer,
      upToOneYear,
      upToTwoYears,
      upToThreeYears,
      afterThreeYears,
    };
  });

  const unreturnedPrinterFeeData = cancellationList.map((cancel) => {
    const printer = cancel.name;
    const unreturnedPrinterFee = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[1]?.['31D-12M']?.fee?.value
    }`;
    return {
      printer,
      unreturnedPrinterFee,
    };
  });

  const cancellationInfos = () => {
    if (unreturnedPrinterFee) {
      return {
        columns: unreturnedPrinterFeeColumns,
        data: unreturnedPrinterFeeData,
      };
    }
    if (hasThreeYearsCancellationSku) {
      return {
        columns: threeYearsColumn,
        data: threeYearsInfoData,
      };
    } else {
      return {
        columns: twoYearsColumn,
        data: twoYearsInfoData,
      };
    }
  };

  const tableText = t('faq.printer.table.text');

  return (
    <div data-testid={'table'} className={styles['table']}>
      {tableText && <div className={styles['table-text']}>{tableText}</div>}
      <Table
        columns={cancellationInfos().columns}
        data={cancellationInfos().data}
        className={
          unreturnedPrinterFee
            ? styles['table-unreturned']
            : styles['table-faq']
        }
      />
    </div>
  );
};
